/**
 * AzureWebPubSubAdapter — `ChatTransport` over one managed WebPubSub connection.
 *
 * Thin by design. Everything about the socket — connecting, reconnecting, token
 * refresh, suspend detection, status — belongs to
 * [ConnectionManager](./ConnectionManager.ts); this file is the seam that turns it
 * into the `ChatTransport` the chat module consumes, and it holds exactly one
 * piece of chat-specific knowledge: pinning the encryption key before a turn's
 * POST goes out.
 */

import type {
  ChatEventListener,
  ChatId,
  ChatTransport,
  ConnectionListener,
  ConnectionStatus,
  TransportHealth,
  Unsubscribe
} from './ChatTransport';
import {
  type ConnectionManager,
  type ConnectionManagerConfig,
  createConnectionManager
} from './ConnectionManager';
import { createEncryptionCodec, type EncryptionCodec } from './EncryptionCodec';
import type { WsUrlProvider } from './ManagedConnection';

export interface AzureWebPubSubAdapterOptions {
  getUrl: WsUrlProvider;
  codec?: EncryptionCodec;
  connectionConfig?: Partial<ConnectionManagerConfig>;
  manager?: ConnectionManager;
}

export function createAzureWebPubSubAdapter(
  opts: AzureWebPubSubAdapterOptions
): ChatTransport {
  const codec = opts.codec ?? createEncryptionCodec();
  const manager =
    opts.manager ??
    createConnectionManager({
      getUrl: opts.getUrl,
      codec,
      config: opts.connectionConfig
    });

  return {
    start() {
      return manager.start();
    },

    stop() {
      return manager.stop();
    },

    subscribe(chatId: ChatId, listener: ChatEventListener): Unsubscribe {
      return manager.subscribe(chatId, listener);
    },

    async openStream(chatId: ChatId): Promise<string> {
      codec.noteCurrentKey?.();
      const connectionId = await manager.ensureConnection();
      manager.markTurnStarted(chatId);
      return connectionId;
    },

    closeStream(chatId: ChatId): void {
      manager.markTurnEnded(chatId);
    },

    getConnectionIdForChat(): string | undefined {
      return manager.getConnectionId();
    },

    onConnectionChange(listener: ConnectionListener): Unsubscribe {
      return manager.onStatusChange(listener);
    },

    getHealth(): TransportHealth {
      const status: ConnectionStatus = manager.getStatus();
      return {
        status,
        connectionId: manager.getConnectionId(),
        activeSubscriptions: manager.getSubscriptionCount()
      };
    }
  };
}
